import { useEffect, useState } from 'react';
import { Modal, Button, FormGroup, ControlLabel, FormControl, Alert, Checkbox } from 'react-bootstrap';

interface IRuleOutReason {
    id: number;
    description: string;
}

interface IProps {
    show: boolean;
    donorName: string;
    ruleOutReasons: IRuleOutReason[];
    preSelectedReasonId?: number;
    onHide: () => void;
    onRuleOut: (reasonId: number, allowReRegistration: boolean, useNkrAssistance: boolean, coordinatorName?: string, phoneNumber?: string, medicalReasonCategory?: number, medicalReasonNotes?: string) => void;
}

interface IState {
    selectedReasonId: number;
    allowReRegistration: boolean;
    useNkrAssistance: boolean;
    coordinatorName?: string;
    phoneNumber?: string;
    selectedMedicalReasonCategory?: number;
    medicalReasonNotes?: string;
    isValid: boolean;
}

const initialState: IState = {
    selectedReasonId: -1,
    allowReRegistration: false,
    useNkrAssistance: false,
    coordinatorName: '',
    phoneNumber: '',
    selectedMedicalReasonCategory: undefined,
    medicalReasonNotes: '',
    isValid: false
};

const RuleOutDonorModal = (props: IProps) => {
    const [state, setState] = useState<IState>(initialState);

    useEffect(() => {
        if (props.show) {
            setState({
                selectedReasonId: props.preSelectedReasonId || -1,
                allowReRegistration: false,
                useNkrAssistance: false,
                coordinatorName: '',
                phoneNumber: '',
                selectedMedicalReasonCategory: undefined,
                medicalReasonNotes: '',
                isValid: props.preSelectedReasonId ? true : false
            });
        }
    }, [props.show, props.preSelectedReasonId]);

    useEffect(() => {
        let isValid = state.selectedReasonId > 0;

        if (state.useNkrAssistance) {
            isValid = isValid && state.coordinatorName && state.coordinatorName.trim() !== '' && state.phoneNumber && state.phoneNumber.trim() !== '';
        }

        if (state.selectedReasonId === 3) {
            isValid = isValid && state.selectedMedicalReasonCategory && state.selectedMedicalReasonCategory > 0;

            if (state.selectedMedicalReasonCategory === 99) {
                isValid = isValid && state.medicalReasonNotes && state.medicalReasonNotes.trim() !== '';
            }
        }

        setState(prev => ({ ...prev, isValid }));
    }, [state.selectedReasonId, state.useNkrAssistance, state.coordinatorName, state.phoneNumber, state.selectedMedicalReasonCategory, state.medicalReasonNotes]);

    const handleReasonChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        let selectedReasonId = parseInt(e.currentTarget.value);

        setState(prev => ({
            ...prev,
            selectedReasonId,
            allowReRegistration: selectedReasonId === 2 ? true : prev.allowReRegistration,
            selectedMedicalReasonCategory: undefined,
            medicalReasonNotes: ''
        }));
    };

    const formatPhoneNumber = (value: string): string => {
        let phoneNumber = value.replace(/\D/g, '');

        if (phoneNumber.length <= 10) {
            if (phoneNumber.length >= 6) {
                return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
            } else if (phoneNumber.length >= 3) {
                return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
            } else if (phoneNumber.length > 0) {
                return `(${phoneNumber}`;
            }
        }

        return value;
    };

    const handlePhoneNumberChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let value = e.currentTarget?.value || '';
        let formattedValue = formatPhoneNumber(value);

        setState(prev => ({ ...prev, phoneNumber: formattedValue }));
    };

    const handleRuleOut = () => {
        if (state.isValid) {
            props.onRuleOut(state.selectedReasonId, state.allowReRegistration, state.useNkrAssistance, state.coordinatorName, state.phoneNumber, state.selectedMedicalReasonCategory, state.medicalReasonNotes);
        } else {
            console.error('Form validation failed. Form is not valid.');
        }
    };

    return (
        <Modal show={props.show} onHide={props.onHide} backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Rule Out Donor</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <FormGroup>
                    <ControlLabel>Disable re-registration with this email address?</ControlLabel>
                    <div>
                        <input
                            type="radio"
                            name="allowReRegistration"
                            id="allowReRegistrationYes"
                            checked={!state.allowReRegistration}
                            onChange={() => setState(prev => ({ ...prev, allowReRegistration: false }))}
                            disabled={state.selectedReasonId === 2}
                            style={{ marginRight: 5 }}
                        />
                        <label htmlFor="allowReRegistrationYes" style={{ marginRight: 15 }}>Yes</label>

                        <input
                            type="radio"
                            name="allowReRegistration"
                            id="allowReRegistrationNo"
                            checked={state.allowReRegistration}
                            onChange={() => setState(prev => ({ ...prev, allowReRegistration: true }))}
                            disabled={state.selectedReasonId === 2}
                            style={{ marginRight: 5 }}
                        />
                        <label htmlFor="allowReRegistrationNo">No</label>
                    </div>
                    {state.selectedReasonId === 2 && (
                        <small style={{ color: '#666', fontStyle: 'italic' }}>
                            Automatically set to "No" when donor dropped out (allows re-registration)
                        </small>
                    )}
                </FormGroup>

                <FormGroup validationState={state.selectedReasonId === -1 ? 'error' : null}>
                    <ControlLabel>Rule out reason:</ControlLabel>
                    <FormControl
                        componentClass="select"
                        value={state.selectedReasonId}
                        onChange={handleReasonChange}
                    >
                        <option value={-1}>- Please Select -</option>
                        <option value={1}>Could Not Contact</option>
                        <option value={2}>Donor Dropped Out</option>
                        <option value={3}>Donor Unsuitable for our Center</option>
                    </FormControl>
                </FormGroup>

                {state.selectedReasonId === 3 && (
                    <FormGroup validationState={state.selectedMedicalReasonCategory && state.selectedMedicalReasonCategory > 0 ? null : 'error'}>
                        <ControlLabel>Medical reason for unsuitability:</ControlLabel>
                        <FormControl
                            componentClass="select"
                            value={state.selectedMedicalReasonCategory || ''}
                            onChange={(e: React.FormEvent<FormControl & HTMLSelectElement>) => {
                                const value = parseInt(e.currentTarget.value);
                                setState(prev => ({
                                    ...prev,
                                    selectedMedicalReasonCategory: value > 0 ? value : undefined,
                                    medicalReasonNotes: value === 99 ? prev.medicalReasonNotes : '' // Clear other reason when not "Other"
                                }));
                            }}
                        >
                            <option value="">- Please Select -</option>
                            <option value={1}>Anatomy</option>
                            <option value={2}>Auto-immune Disease</option>
                            <option value={3}>Psychiatric history</option>
                            <option value={4}>Cardiovascular risks</option>
                            <option value={5}>Renal function below acceptable threshold</option>
                            <option value={6}>Genetic kidney disease</option>
                            <option value={7}>Infections (Hep B, Hep C, HIV, etc.)</option>
                            <option value={99}>Other</option>
                        </FormControl>
                    </FormGroup>
                )}

                {state.selectedReasonId === 3 && state.selectedMedicalReasonCategory === 99 && (
                    <FormGroup validationState={state.medicalReasonNotes && state.medicalReasonNotes.trim() !== '' ? null : 'error'}>
                        <ControlLabel>Please specify the medical reason:</ControlLabel>
                        <FormControl
                            type="text"
                            placeholder="Enter the specific medical reason"
                            value={state.medicalReasonNotes || ''}
                            onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => {
                                const value = e.currentTarget?.value || '';
                                setState(prev => ({ ...prev, medicalReasonNotes: value }));
                            }}
                        />
                    </FormGroup>
                )}

                {state.selectedReasonId === 1 && (
                    <FormGroup>
                        <ControlLabel>Use NKR's assistance contacting the donor?</ControlLabel>
                        <div>
                            <input
                                type="radio"
                                name="useNkrAssistance"
                                id="useNkrAssistanceYes"
                                checked={state.useNkrAssistance}
                                onChange={() => setState(prev => ({ ...prev, useNkrAssistance: true }))}
                                style={{ marginRight: 5 }}
                            />
                            <label htmlFor="useNkrAssistanceYes" style={{ marginRight: 15 }}>Yes</label>

                            <input
                                type="radio"
                                name="useNkrAssistance"
                                id="useNkrAssistanceNo"
                                checked={!state.useNkrAssistance}
                                onChange={() => setState(prev => ({ ...prev, useNkrAssistance: false }))}
                                style={{ marginRight: 5 }}
                            />
                            <label htmlFor="useNkrAssistanceNo">No</label>
                        </div>

                        {state.useNkrAssistance && (
                            <div style={{ marginTop: 15 }}>
                                <Alert bsStyle="warning" style={{ marginBottom: 15 }}>
                                    The donor will be emailed the below from an NKR domain email address. Please enter the missing contact information below.
                                </Alert>

                                <div style={{
                                    border: '1px solid #ccc',
                                    padding: 15,
                                    backgroundColor: '#f9f9f9',
                                    marginBottom: 15,
                                    fontFamily: 'serif'
                                }}>
                                    <p><strong>Dear <u>{props.donorName}</u>,</strong></p>
                                    <p style={{ marginBottom: 15 }}>
                                        Thank you for your interest in donating. After multiple attempts, the center was unable to establish contact with you. If you are still interested in donating, please call the number below to contact your coordinator if you wish to continue with your donation process.
                                    </p>

                                    <FormGroup>
                                        <ControlLabel>Coordinator's Name:</ControlLabel>
                                        <FormControl
                                            type="text"
                                            placeholder="Enter coordinator's name"
                                            value={state.coordinatorName || ''}
                                            onChange={(e: React.FormEvent<FormControl & HTMLInputElement>) => {
                                                const value = e.currentTarget?.value || '';
                                                setState(prev => ({ ...prev, coordinatorName: value }));
                                            }}
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <ControlLabel>Phone #:</ControlLabel>
                                        <FormControl
                                            type="tel"
                                            placeholder="(555) 555-5555"
                                            value={state.phoneNumber || ''}
                                            onChange={handlePhoneNumberChange}
                                            maxLength={14}
                                            pattern="\\([0-9]{3}\\) [0-9]{3}-[0-9]{4}"
                                        />
                                        <small style={{ color: '#666', fontStyle: 'italic' }}>
                                            Format: (XXX) XXX-XXXX
                                        </small>
                                    </FormGroup>

                                    <p style={{ marginTop: 15, marginBottom: 0 }}><strong>Best,<br />National Kidney Registry</strong></p>
                                </div>

                            </div>
                        )}
                    </FormGroup>
                )}

                <div style={{ marginTop: 20, textAlign: 'right' }}>
                    <Button
                        bsStyle="primary"
                        disabled={!state.isValid}
                        onClick={handleRuleOut}
                        style={{ marginRight: 10 }}
                    >
                        Rule Out
                    </Button>
                    <Button onClick={props.onHide}>
                        Cancel
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default RuleOutDonorModal;